// @ts-nocheck
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

import { FProgressBar } from '../components/base';
import { useSidebar } from '../contexts';
import { authActions } from '../store';
import { history, areUserRolesIn } from '../helpers';
import SideBar from '../components/layout/sidebar';
import Header from '../components/layout/header/Header';
import { HEADER_HEIGHT, Roles, SIDEBAR_WIDTH } from '../constants';
import { useAuthUser, usePreClinicalGuardianGating } from '../hooks';

import type { PrivateRouteProps } from './types';

function PrivateRoute({
  roles: routeRoles,
  children,
  hideForPreClinicalGuardian = false,
}: PrivateRouteProps) {
  const { isAuthenticated, isLoading, user, loginWithRedirect, getAccessTokenSilently } =
    useAuth0();
  const { dispatch, authUser } = useAuthUser();
  const { hideClinicalFeatures, isClinicalGatingPending } = usePreClinicalGuardianGating();
  const [authLoading, setAuthLoading] = useState<boolean>(true);

  const navigate = useNavigate();
  const { sidebarWidth, isSidebarToggled } = useSidebar();

  const shouldBlockPreClinicalGuardian =
    hideForPreClinicalGuardian && !isClinicalGatingPending && hideClinicalFeatures;

  const isUnauthorizedByRole =
    Boolean(routeRoles) &&
    (!authUser?.roles
      ? !authLoading
      : !routeRoles.some((routeRole) => authUser.roles.includes(routeRole)));

  useEffect(() => {
    if (isLoading) return;

    const getAccessToken = async () => {
      const accessToken = await getAccessTokenSilently();
      const decoded = jwtDecode<{ user_roles?: Roles[] }>(accessToken);

      // A user can have multiple roles
      const userRolesFromToken = decoded.user_roles;

      // TODO: Define user type
      dispatch(
        authActions.setUser({
          user: { ...user, roles: userRolesFromToken },
        }),
      );
      setAuthLoading(false);
    };

    if (!isAuthenticated || !user) {
      const query = window.location.search;
      if (query.includes('error=access_denied&error_description=Signup%20Disabled')) {
        navigate('/social-signup-disabled', { replace: true });
      } else {
        sessionStorage.clear();
        loginWithRedirect({ appState: { returnTo: window.location.href } });
      }
    } else {
      getAccessToken();
    }
  }, [isAuthenticated, user, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!routeRoles || authLoading) return;

    if (!authUser?.roles) {
      history.navigate('/dashboard');
      toast.error('User does not have appropriate role', { toastId: 'unauthorizedRouteError' });
      return;
    }

    // if user not authorized, redirect to dashboard
    if (!routeRoles.some((routeRole) => authUser?.roles.includes(routeRole))) {
      const redirectPath = authUser?.roles.includes(Roles.OnboardingCoordinator)
        ? '/admin'
        : authUser?.roles.includes(Roles.Biller)
        ? '/billings/claims'
        : authUser?.roles.includes(Roles.InvoiceCoordinator)
        ? '/billings/invoice-builder'
        : '/dashboard';
      history.navigate(redirectPath);
      toast.error('User does not have appropriate role', { toastId: 'unauthorizedRouteError' });
    }

    if (areUserRolesIn(authUser?.roles, [Roles.Biller]) && window.location.pathname === '/') {
      history.navigate('/billings/claims');
    }

    if (
      areUserRolesIn(authUser?.roles, [Roles.InvoiceCoordinator]) &&
      window.location.pathname === '/'
    ) {
      history.navigate('/billings/invoice-builder');
    }

    if (
      areUserRolesIn(authUser?.roles, [Roles.OnboardingCoordinator]) &&
      window.location.pathname === '/'
    ) {
      history.navigate('/admin');
    }
  }, [routeRoles, authUser?.roles, authLoading]);

  useEffect(() => {
    if (!shouldBlockPreClinicalGuardian) return;

    history.navigate('/dashboard');
    toast.error('This page is not available for your clinical status', {
      toastId: 'preClinicalGuardianRouteError',
    });
  }, [shouldBlockPreClinicalGuardian]);

  if (authLoading || isUnauthorizedByRole) return <FProgressBar />;

  return (
    <Box sx={{ display: 'flex' }}>
      <SideBar />
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          transition: 'all 0.3s ease-in-out',
          marginLeft: { sm: 0, md: isSidebarToggled ? `${SIDEBAR_WIDTH}px` : '100px' },
          width: `calc(100vw - ${sidebarWidth}px)`,
          boxSizing: 'border-box',
        }}
      >
        <Header />
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          }}
        >
          {(hideForPreClinicalGuardian && isClinicalGatingPending) ||
          shouldBlockPreClinicalGuardian ? (
            <FProgressBar />
          ) : (
            children
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default PrivateRoute;
